// lib/services/compute/rb_log.ts
//
// Lightweight structured logging for the GPU readback path. Logging only:
// these helpers never change control flow, slot state, or recovery behavior.
// Each entry is emitted as ONE copy/paste-friendly line: the `[RB]` prefix
// followed by a single JSON string (no console object, no multi-line output).
// Never log sticker payloads or user data — only sizes, states, and timings.

const PREFIX = "[RB]";

// ---- TEMPORARY logging switch (revert by setting this back to true) --------
// Master toggle for INFORMATIONAL capture/recognition/readback logging.
// When false: rb_log() and rb_debug_log() are silenced so the iOS test
// console only shows real errors/warnings. rb_error() is NEVER gated by this
// and always emits. To re-enable all info logging, flip this single constant
// to true.
export const RB_INFO_ENABLED = false;

function now_ms(): number {
  return Math.round(performance.now() * 10) / 10;
}

// JSON.stringify replacer that never throws:
// - bigint -> decimal string with an "n" suffix (e.g. 42n -> "42n")
// - Error  -> { name, message, stack }
// - circular references -> "[Circular]"
// Opaque objects (e.g. WebGPU handles) are not expected in payloads and are
// left to default serialization; circular guarding keeps that safe.
function make_replacer(): (key: string, value: unknown) => unknown {
  const seen = new WeakSet<object>();
  return function (_key: string, value: unknown): unknown {
    if (typeof value === "bigint") {
      return `${value.toString()}n`;
    }
    if (value instanceof Error) {
      return { name: value.name, message: value.message, stack: value.stack };
    }
    if (typeof value === "object" && value !== null) {
      if (seen.has(value)) return "[Circular]";
      seen.add(value);
    }
    return value;
  };
}

function serialize(event: string, data: Record<string, unknown>): string {
  const t = now_ms();
  const payload = { event, t, ...data };
  try {
    return `${PREFIX} ${JSON.stringify(payload, make_replacer())}`;
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    return `${PREFIX} ${JSON.stringify({ event, t, _serialize_error: message })}`;
  }
}

export function rb_log(event: string, data: Record<string, unknown>): void {
  if (!RB_INFO_ENABLED) return;
  console.log(serialize(event, data));
}

// Gated passthrough for legacy free-form console.log info messages in the
// capture/recognition/readback path. Same signature/behavior as console.log
// (variadic, preserves objects) but silenced when RB_INFO_ENABLED is false.
// rb_error / console.error / real console.warn are intentionally NOT routed
// through here so failures always surface.
export function rb_debug_log(...args: unknown[]): void {
  if (!RB_INFO_ENABLED) return;
  console.log(...args);
}

export function rb_error(event: string, data: Record<string, unknown>): void {
  console.error(serialize(event, data));
}

/** Safe mapState read for logging; never throws. */
export function rb_map_state(buffer: GPUBuffer | null): string {
  try {
    return buffer?.mapState ?? "none";
  } catch {
    return "err";
  }
}

// Per-key rate limiter. Returns true at most once per interval for a key,
// so recurring diagnostics (census, success timings) don't spam every frame.
const last_emit = new Map<string, number>();

export function rb_throttle(key: string, interval_ms = 1000): boolean {
  const now = performance.now();
  const prev = last_emit.get(key) ?? 0;
  if (now - prev >= interval_ms) {
    last_emit.set(key, now);
    return true;
  }
  return false;
}
